import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { parsePlacementText } from '../engine/parsers';
import { Loader2, Upload } from 'lucide-react';
import { invokeEdgeFunction } from '@/lib/edgeFunctionRetry';
import { HouseSystemSelector } from './HouseSystemSelector';
import type { HouseSystem } from '../engine/houses';

export interface BirthFormValue {
  name: string;
  year: number; month: number; day: number;
  hour: number; minute: number;
  tzOffset: number; lat: number; lon: number;
  placeName?: string;
  ianaTz?: string;
  unknownTime?: boolean;
  houseSystem?: HouseSystem;
}

interface Props {
  onSubmit: (value: BirthFormValue) => void;
  onImportPositions?: (positions: Record<string, number>, houseSystem?: HouseSystem) => void;
  initialValue?: Partial<BirthFormValue>;
  submitLabel?: string;
}

function offsetHoursFor(iana: string, y: number, m: number, d: number, h = 12, mi = 0): number {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      timeZoneName: 'longOffset',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    const probe = new Date(Date.UTC(y, m - 1, d, h, mi));
    const parts = fmt.formatToParts(probe);
    const tzName = parts.find(p => p.type === 'timeZoneName')?.value ?? 'GMT+00:00';
    const m2 = tzName.match(/GMT([+\-\u2212])(\d{1,2})(?::(\d{2}))?/);
    if (!m2) return 0;
    const sign = m2[1] === '+' ? 1 : -1;
    const hh = parseInt(m2[2], 10);
    const mm = m2[3] ? parseInt(m2[3], 10) : 0;
    return sign * (hh + mm / 60);
  } catch {
    return 0;
  }
}

export const BirthForm: React.FC<Props> = ({ onSubmit, onImportPositions, initialValue, submitLabel }) => {
  const { toast } = useToast();
  const [value, setValue] = useState<BirthFormValue>({
    name: '', year: 1990, month: 1, day: 1, hour: 12, minute: 0,
    tzOffset: 0, lat: 0, lon: 0, placeName: '', houseSystem: 'placidus',
    ...(initialValue ?? {}),
  });
  const [city, setCity] = useState('');
  const [searching, setSearching] = useState(false);
  const [cityResults, setCityResults] = useState<Array<{
    name: string; admin1?: string; country?: string; country_code?: string;
    latitude: number; longitude: number; timezone?: string;
  }>>([]);
  const [searchedEmpty, setSearchedEmpty] = useState(false);
  const [pasted, setPasted] = useState('');
  const [ocrLoading, setOcrLoading] = useState(false);

  const set = <K extends keyof BirthFormValue>(k: K, v: BirthFormValue[K]) =>
    setValue(prev => ({ ...prev, [k]: v }));

  // Re-derive offset when date or zone changes (DST-aware).
  useEffect(() => {
    if (!value.ianaTz) return;
    const next = offsetHoursFor(value.ianaTz, value.year, value.month, value.day, value.hour, value.minute);
    if (next !== value.tzOffset) {
      setValue(prev => ({ ...prev, tzOffset: next }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.ianaTz, value.year, value.month, value.day]);

  const pickCity = (r: {
    name: string; admin1?: string; country?: string; country_code?: string;
    latitude: number; longitude: number; timezone?: string;
  }) => {
    const parts = [r.name, r.admin1, r.country].filter(Boolean);
    const placeName = parts.join(', ');
    const offset = r.timezone
      ? offsetHoursFor(r.timezone, value.year, value.month, value.day, value.hour, value.minute)
      : value.tzOffset;
    setValue(prev => ({
      ...prev,
      lat: r.latitude,
      lon: r.longitude,
      placeName,
      ianaTz: r.timezone || prev.ianaTz,
      tzOffset: offset,
    }));
    setCityResults([]);
    setSearchedEmpty(false);
    const sign = offset >= 0 ? '+' : '';
    toast({ title: 'Location found', description: `${placeName} · UTC${sign}${offset}` });
  };

  const searchCity = async () => {
    if (!city.trim()) return;
    setSearching(true);
    setSearchedEmpty(false);
    try {
      // Split "Torrance, CA" or "Berlin, Germany" — send only the city part
      // to Open-Meteo and filter results by the region hint on the client.
      const parts = city.split(',').map((s) => s.trim()).filter(Boolean);
      const cityName = parts[0];
      const regionHint = parts[1]?.toLowerCase();
      const res = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(cityName)}&count=8`
      );
      const data = await res.json();
      const all: Array<{
        name: string; admin1?: string; country?: string; country_code?: string;
        latitude: number; longitude: number; timezone?: string;
      }> = data.results ?? [];

      const filtered = regionHint
        ? all.filter((r) => {
            const hay = [r.admin1, r.country, r.country_code]
              .filter(Boolean)
              .map((s) => (s as string).toLowerCase());
            return hay.some((s) => s.includes(regionHint) || s.startsWith(regionHint));
          })
        : all;

      const results = filtered.length ? filtered : all;

      if (results.length === 0) {
        setSearchedEmpty(true);
        toast({ title: 'Not found', description: 'Try "City, Country" or a nearby larger city.', variant: 'destructive' });
      } else if (results.length === 1) {
        pickCity(results[0]);
      } else {
        setCityResults(results.slice(0, 5));
      }
    } catch {
      toast({ title: 'Geocoding failed', variant: 'destructive' });
    } finally {
      setSearching(false);
    }
  };

  const handlePasteImport = () => {
    const positions = parsePlacementText(pasted);
    if (!positions) {
      toast({ title: 'Could not parse', description: 'Try a format like "Sun 14 Leo 23"', variant: 'destructive' });
      return;
    }
    onImportPositions?.(positions, value.houseSystem ?? 'whole');
  };

  const handleImageUpload = async (file: File) => {
    setOcrLoading(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1] ?? '');
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const data = await invokeEdgeFunction<{ text: string }>('astrology-ocr-chart', { body: { imageBase64: base64 } });
      const text = data?.text ?? '';
      setPasted((prev) => (prev ? prev + '\n' : '') + text);
      const positions = parsePlacementText(text);
      if (positions) {
        toast({ title: 'Chart parsed', description: 'Review the extracted text below before importing.' });
      } else {
        toast({ title: 'Extracted text', description: 'Review and edit before importing.' });
      }
    } catch (e) {
      toast({ title: 'OCR failed', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setOcrLoading(false);
    }
  };

  return (
    <Card className="p-6 space-y-6">
      <Tabs defaultValue="birth">
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="birth">Birth data</TabsTrigger>
          <TabsTrigger value="paste">Paste chart</TabsTrigger>
        </TabsList>

        <TabsContent value="birth" className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Name (optional)</Label>
            <Input value={value.name} onChange={e => set('name', e.target.value)} placeholder="self" />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div><Label>Year</Label><Input type="number" inputMode="numeric" value={value.year || ''} onChange={e => set('year', parseInt(e.target.value) || 0)} /></div>
            <div><Label>Month</Label><Input type="number" inputMode="numeric" min={1} max={12} value={value.month || ''} onChange={e => set('month', parseInt(e.target.value) || 0)} /></div>
            <div><Label>Day</Label><Input type="number" inputMode="numeric" min={1} max={31} value={value.day || ''} onChange={e => set('day', parseInt(e.target.value) || 0)} /></div>
          </div>

          <div className="flex items-center gap-2 -mt-1">
            <Switch
              id="unknown-time"
              checked={!!value.unknownTime}
              onCheckedChange={(checked) => setValue(prev => ({ ...prev, unknownTime: checked }))}
            />
            <Label htmlFor="unknown-time" className="text-sm font-normal cursor-pointer">
              I don't know my birth time (Rising, houses, and Midheaven will be hidden)
            </Label>
          </div>

          <div className={`grid grid-cols-3 gap-2 ${value.unknownTime ? 'opacity-50 pointer-events-none' : ''}`}>
            <div><Label>Hour (0-23)</Label><Input type="number" inputMode="numeric" min={0} max={23} value={value.hour === 0 ? '0' : (value.hour || '')} onChange={e => set('hour', parseInt(e.target.value) || 0)} /></div>
            <div><Label>Minute</Label><Input type="number" inputMode="numeric" min={0} max={59} value={value.minute === 0 ? '0' : (value.minute || '')} onChange={e => set('minute', parseInt(e.target.value) || 0)} /></div>
            <div>
              <Label>UTC offset</Label>
              <Input
                type="number"
                step={0.5}
                value={String(value.tzOffset)}
                onChange={e => {
                  // Manual edit overrides auto-detection
                  setValue(prev => ({ ...prev, tzOffset: parseFloat(e.target.value) || 0, ianaTz: undefined }));
                }}
              />
            </div>
          </div>

          <div className={`flex items-center justify-between gap-3 ${value.unknownTime ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="space-y-1">
              <Label htmlFor="house-system" className="text-xs">House system</Label>
              <p className="text-[10px] text-muted-foreground leading-tight">
                Different systems divide houses differently.
              </p>
            </div>
            <HouseSystemSelector
              id="house-system"
              value={value.houseSystem ?? 'placidus'}
              onChange={(houseSystem) => setValue(prev => ({ ...prev, houseSystem }))}
              disabled={!!value.unknownTime}
            />
          </div>

          {value.ianaTz && (
            <p className="text-xs text-muted-foreground -mt-2">
              Auto-detected timezone for {value.placeName || value.ianaTz} on {value.year}-{String(value.month).padStart(2, '0')}-{String(value.day).padStart(2, '0')} (UTC{value.tzOffset >= 0 ? '+' : ''}{value.tzOffset}).{' '}
              <button
                type="button"
                className="underline hover:text-foreground"
                onClick={() => setValue(prev => ({ ...prev, ianaTz: undefined }))}
              >
                Override
              </button>
            </p>
          )}

          <div className="space-y-2">
            <Label>Birth city</Label>
            <div className="flex gap-2">
              <Input
                value={city}
                onChange={e => setCity(e.target.value)}
                placeholder="e.g. Torrance, CA or Berlin, Germany"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchCity(); } }}
              />
              <Button type="button" onClick={searchCity} disabled={searching} variant="outline">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
            {cityResults.length > 0 && (
              <div className="border border-border rounded-md divide-y divide-border/60 bg-background/40">
                {cityResults.map((r, i) => (
                  <button
                    key={`${r.name}-${r.latitude}-${r.longitude}-${i}`}
                    type="button"
                    onClick={() => pickCity(r)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted/40 focus:bg-muted/40 focus:outline-none"
                  >
                    <div className="font-medium">{[r.name, r.admin1, r.country].filter(Boolean).join(', ')}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.latitude.toFixed(2)}° {r.latitude >= 0 ? 'N' : 'S'}, {Math.abs(r.longitude).toFixed(2)}° {r.longitude >= 0 ? 'E' : 'W'}
                      {r.timezone ? ` · ${r.timezone}` : ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {searchedEmpty && cityResults.length === 0 && (
              <p className="text-xs text-muted-foreground">Not found? Try "City, Country" or a nearby larger city.</p>
            )}
            {value.placeName && (
              <p className="text-xs text-muted-foreground">
                {value.placeName} · {Math.abs(value.lat).toFixed(2)}° {value.lat >= 0 ? 'N' : 'S'}, {Math.abs(value.lon).toFixed(2)}° {value.lon >= 0 ? 'E' : 'W'}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div><Label>Latitude</Label><Input type="number" step={0.01} value={value.lat} onChange={e => set('lat', parseFloat(e.target.value) || 0)} /></div>
            <div><Label>Longitude</Label><Input type="number" step={0.01} value={value.lon} onChange={e => set('lon', parseFloat(e.target.value) || 0)} /></div>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">
            West longitude and South latitude are negative (e.g. Los Angeles is around 34° N, −118° W).
          </p>

          <Button
            onClick={() => {
              // Validate date
              const d = new Date(value.year, value.month - 1, value.day);
              if (
                d.getFullYear() !== value.year ||
                d.getMonth() !== value.month - 1 ||
                d.getDate() !== value.day
              ) {
                toast({ title: 'Invalid date', description: 'That date does not exist on the calendar.', variant: 'destructive' });
                return;
              }
              if (d.getTime() > Date.now()) {
                toast({ title: 'Future date', description: 'Birth date cannot be in the future.', variant: 'destructive' });
                return;
              }
              onSubmit(value);
            }}
            className="w-full"
          >
            {submitLabel ?? 'Cast chart'}
          </Button>
        </TabsContent>

        <TabsContent value="paste" className="space-y-4 pt-4">
          <p className="text-sm text-muted-foreground">Paste placements from any chart calculator. Format like <code>Sun 14 Leo 23</code> per line.</p>
          <div className="flex items-center gap-2">
            <label className="flex-1">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageUpload(f); }}
              />
              <span className="inline-flex items-center justify-center gap-2 w-full h-9 rounded-md border border-border bg-background hover:bg-muted text-sm cursor-pointer">
                {ocrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Upload chart screenshot
              </span>
            </label>
          </div>
          <Textarea rows={10} value={pasted} onChange={e => setPasted(e.target.value)} className="font-mono text-xs" />
          <Button onClick={handlePasteImport} className="w-full" disabled={!pasted.trim()}>Import positions</Button>
        </TabsContent>
      </Tabs>
    </Card>
  );
};